/*
 * Atolyewolf – harfli ürünlerde canlı charm önizleme (snippets/aw-letter-preview.liquid)
 *
 * 1. charm = seçili varyantın Harf/Rakam/Burç değeri (ürün fotoğrafında zaten var).
 * 2–4. charm = ekstra harf bloğu (window.__awExtraLetters.state.letters[2..4]).
 * Ek charm yokken: gerçek ürün fotoğrafı olduğu gibi kalır.
 * Ek charm varken: varyant fotoğrafı aynı çekimin boş bileklik görseline geçer, 1. charm + ek charm'lar ipe ortalanarak
 * yan yana çizilir (charm genişliğine göre, aralarında config.gap px). Koordinatlar 2000×2000 fotoğraf düzleminde;
 * ip eğrisi polinomla (coef) verilir. Ürün görseli ekrandan çıkınca üstte sabit önizleme şeridi gösterilir.
 */

(() => {
  class AwLetterPreview extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      try {
        this.config = JSON.parse(this.querySelector('[data-aw-lp-config]').textContent);
        this.data = JSON.parse(this.querySelector('[data-aw-lp-data]').textContent);
      } catch (error) {
        console.warn('[aw-letter-preview] config error', error);
        return;
      }

      if (!this.config.combos[this.data.combo]) return;

      this.section = this.closest('.shopify-section') || document;
      this.floatElement = this.querySelector('[data-aw-lp-float]');
      this.floatLayer = this.querySelector('[data-aw-lp-layer]');
      this.swapped = null;

      const schedule = () => this.scheduleRender();

      this.section.addEventListener('click', schedule, { signal });
      this.section.addEventListener('change', schedule, { signal });
      document.addEventListener('variant:change', schedule, { signal });
      window.addEventListener('resize', schedule, { signal });
      window.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateFloat();
        });
      }, { passive: true, signal });

      // Ekstra harf bloğu seçimleri aria-pressed/hidden ile boyar → değişiklikleri izle
      this.extraObserver = new MutationObserver(schedule);
      this.section.querySelectorAll('[data-aw-extra]').forEach((element) => {
        this.extraObserver.observe(element, { subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'hidden'] });
      });

      // Galeri yeniden çizilirse (varyant değişimi) katmanı tekrar kur
      const gallery = this.section.querySelector('product-gallery');
      if (gallery) {
        this.galleryObserver = new MutationObserver(schedule);
        this.galleryObserver.observe(gallery, { childList: true, subtree: true });
      }

      this.resizeObserver = new ResizeObserver(() => this.positionLayer());
      this.render();
    }

    // Varyant değişince tema ürün bilgisini yeniden çizer ve bu öğe yenisiyle değişir: bekleyen çizimleri iptal et,
    // DOM'a dokunma (yeni öğe ilk çiziminde eski katmanları temizler, görselleri geri yükler → titreme olmaz)
    disconnectedCallback() {
      this.abortController?.abort();
      this.extraObserver?.disconnect();
      this.galleryObserver?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
    }

    scheduleRender() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);

      if (document.hidden) {
        this.timer = setTimeout(() => this.render(), 0);
      } else {
        this.frame = requestAnimationFrame(() => this.render());
      }
    }

    get form() {
      return this.section.querySelector('product-form form[action*="/cart/add"]') || this.section.querySelector('form[action*="/cart/add"]');
    }

    variant() {
      const id = this.form ? new FormData(this.form).get('id') : null;
      return (id && this.data.variants[String(id)]) || Object.values(this.data.variants)[0] || null;
    }

    // [{ slot, value }] – 1. charm varyanttan, 2–4 ekstra harf bloğundan
    selection(variant) {
      const items = [];
      const letters = window.__awExtraLetters?.state?.letters || {};

      if (variant?.v) items.push({ slot: '1', value: variant.v });

      for (let slot = 2; slot <= 4; slot++) {
        if (letters[slot]) items.push({ slot: String(slot), value: letters[slot] });
      }

      return items;
    }

    /* Galeri: varyant görselini boş zemine çevir / geri al */

    cellFor(mediaId) {
      return mediaId ? this.section.querySelector(`.product-gallery__media[data-media-id="${mediaId}"]`) : null;
    }

    // Orijinal src/srcset görselin kendisinde saklanır: hangi öğe örneği değiştirmiş olursa olsun geri yüklenebilir
    swap(cell) {
      const image = cell?.querySelector('img');
      if (!image) return null;

      if (!('awLpOrigSrc' in image.dataset)) {
        image.dataset.awLpOrigSrc = image.getAttribute('src') || '';
        image.dataset.awLpOrigSrcset = image.getAttribute('srcset') || '';
      }

      if (image.getAttribute('src') !== this.data.base) {
        image.setAttribute('src', this.data.base);
        image.setAttribute('srcset', this.data.baseSrcset);
        image.setAttribute('loading', 'eager');
      }

      this.swapped = image;
      return image;
    }

    restoreImage(image) {
      if (!image || !('awLpOrigSrc' in image.dataset)) return;

      if (image.dataset.awLpOrigSrc) image.setAttribute('src', image.dataset.awLpOrigSrc);
      if (image.dataset.awLpOrigSrcset) image.setAttribute('srcset', image.dataset.awLpOrigSrcset); else image.removeAttribute('srcset');
      delete image.dataset.awLpOrigSrc;
      delete image.dataset.awLpOrigSrcset;
    }

    // keepImage dışındaki tüm değiştirilmiş görselleri geri yükle, keepLayer dışındaki tüm galeri katmanlarını kaldır
    cleanup(keepImage = null, keepLayer = null) {
      this.section.querySelectorAll('.product-gallery__media img[data-aw-lp-orig-src]').forEach((image) => {
        if (image !== keepImage) this.restoreImage(image);
      });

      this.section.querySelectorAll('.product-gallery__media .aw-lp__layer').forEach((layer) => {
        if (layer !== keepLayer) layer.remove();
      });

      if (!keepLayer) {
        this.layer = null;
        this.layerImage = null;
        this.resizeObserver?.disconnect();
      }

      if (!keepImage) this.swapped = null;
    }

    restore() {
      this.cleanup(null, null);
    }

    ensureLayer(image) {
      if (this.layer && this.layer.isConnected && this.layerImage === image) return this.layer;

      this.layer?.remove();
      this.layerImage = image;

      const container = image.parentElement;
      if (getComputedStyle(container).position === 'static') container.style.position = 'relative';

      this.layer = document.createElement('div');
      this.layer.className = 'aw-lp__layer';
      this.layer.setAttribute('aria-hidden', 'true');
      container.append(this.layer);

      this.resizeObserver.disconnect();
      this.resizeObserver.observe(image);
      this.positionLayer();

      return this.layer;
    }

    positionLayer() {
      const image = this.layerImage;
      const layer = this.layer;
      if (!image || !layer) return;

      Object.assign(layer.style, {
        left: `${image.offsetLeft}px`,
        top: `${image.offsetTop}px`,
        width: `${image.offsetWidth}px`,
        height: `${image.offsetHeight}px`
      });
    }

    /* Sabit önizleme şeridi: ek charm seçimi sürerken ürün görselinin bileklik bandı ekranda değilse */

    updateFloat() {
      if (!this.floatElement) return;

      const extra = this.section.querySelector('[data-aw-extra]');
      const extraCount = this.lastItems ? this.lastItems.length - 1 : 0;
      const pickerOpen = (window.__awExtraLetters?.state?.open || 0) > 0;
      let show = false;

      if (extra && (extraCount > 0 || pickerOpen)) {
        const headerBottom = Math.max(0, document.querySelector('.shopify-section--header')?.getBoundingClientRect().bottom || 0);
        const cell = this.cellFor(this.lastVariant?.m);
        const image = cell?.querySelector('img');
        const rect = image?.getBoundingClientRect();
        // Bileklik bandı: fotoğrafın %41–67 yüksekliği
        const bandVisible = rect && rect.width > 0 && rect.top + rect.height * 0.67 > headerBottom + 20 && rect.top + rect.height * 0.41 < window.innerHeight;
        const extraRect = extra.getBoundingClientRect();
        const inBuilder = extraRect.top < window.innerHeight && extraRect.bottom > headerBottom + 120;
        show = !bandVisible && inBuilder;

        if (show && window.matchMedia('(min-width: 1000px)').matches) {
          const gallery = this.section.querySelector('product-gallery');
          const g = gallery?.getBoundingClientRect();
          if (g && g.width > 0) {
            this.floatElement.style.setProperty('--aw-lp-float-left', `${Math.max(16, g.left)}px`);
            this.floatElement.style.setProperty('--aw-lp-float-width', `${Math.min(520, g.width)}px`);
          }
        }
      }

      this.floatElement.hidden = !show;
    }

    /* Çizim */

    poly(coefficients, x) {
      return coefficients.reduce((sum, c) => sum * x + c, 0);
    }

    layout(items) {
      const { values: valueMap, gap, center } = this.config;
      const { charms, coef } = this.config.combos[this.data.combo];
      const derivative = coef.slice(0, -1).map((c, i, arr) => c * (arr.length - i));
      const placeable = items.map((item) => ({ ...item, key: valueMap[item.value] })).filter((item) => item.key && charms[item.key]);
      const total = placeable.reduce((sum, item) => sum + charms[item.key][2], 0) + gap * Math.max(placeable.length - 1, 0);
      let cursor = center - total / 2;

      return placeable.map((item) => {
        const [sx, sy, w, h, ax, ay] = charms[item.key];
        const cx = cursor + ax;
        const cy = this.poly(coef, cx);
        const angle = Math.atan(this.poly(derivative, cx)) * 180 / Math.PI;
        cursor += w + gap;
        return { ...item, sx, sy, w, h, ax, ay, cx, cy, angle };
      });
    }

    paint(layer, placed) {
      if (!layer) return;

      const size = this.config.size;
      const [spriteW, spriteH] = this.config.combos[this.data.combo].sprite;
      const existing = new Map([...layer.children].map((child) => [child.dataset.slot, child]));

      placed.forEach((item) => {
        let element = existing.get(item.slot);
        existing.delete(item.slot);

        if (!element) {
          element = document.createElement('span');
          element.className = 'aw-lp__charm';
          element.dataset.slot = item.slot;
          layer.append(element);
        }

        if (element.dataset.key !== item.key) {
          const first = !element.dataset.key;
          element.dataset.key = item.key;
          // 1. charm fotoğrafta zaten vardı: düşme animasyonu yalnızca yeni eklenen charm'larda
          if (!(first && item.slot === '1')) {
            element.classList.remove('is-entering');
            void element.offsetWidth;
            element.classList.add('is-entering');
          }
        }

        Object.assign(element.style, {
          left: `${(item.cx - item.ax) / size * 100}%`,
          top: `${(item.cy - item.ay) / size * 100}%`,
          width: `${item.w / size * 100}%`,
          height: `${item.h / size * 100}%`,
          backgroundImage: `url("${this.data.sprite}")`,
          backgroundSize: `${spriteW / item.w * 100}% ${spriteH / item.h * 100}%`,
          backgroundPosition: `${spriteW === item.w ? 0 : item.sx / (spriteW - item.w) * 100}% ${spriteH === item.h ? 0 : item.sy / (spriteH - item.h) * 100}%`,
          transformOrigin: `${item.ax / item.w * 100}% ${item.ay / item.h * 100}%`,
          transform: `rotate(${item.angle.toFixed(2)}deg)`
        });
      });

      existing.forEach((element) => element.remove());
    }

    render() {
      if (!this.isConnected) return;

      const variant = this.variant();
      const items = this.selection(variant);
      const placed = this.layout(items);
      const hasExtras = items.some((item) => item.slot !== '1');

      this.lastVariant = variant;
      this.lastItems = items;

      const cell = this.cellFor(variant?.m);

      if (hasExtras && cell) {
        const image = this.swap(cell);
        if (image) {
          const layer = this.ensureLayer(image);
          this.cleanup(image, layer);
          this.paint(layer, placed);
          this.positionLayer();
        } else {
          this.restore();
        }
      } else {
        this.restore();
      }

      this.paint(this.floatLayer, placed);
      this.updateFloat();

      const caption = this.querySelector('[data-aw-lp-caption]');
      if (caption) {
        caption.textContent = hasExtras
          ? 'Canlı önizleme: eklediğiniz charm\'lar ürün görselinde yan yana gösterilir.'
          : 'Ek charm eklediğinizde bileklikte yan yana canlı görürsünüz.';
      }
    }
  }

  if (!window.customElements.get('aw-letter-preview')) {
    window.customElements.define('aw-letter-preview', AwLetterPreview);
  }
})();
